// Package imports:
import React, { useEffect, useRef, useState } from 'react';
import Highcharts from 'highcharts/highstock';
import { format } from 'date-fns';
import ISLocale from 'date-fns/locale/is';
// Component imports:
import DisplayBox from '../../DisplayBox/DisplayBox';
import ChartLoading from '../../../ui-elements/ChartLoading/ChartLoading';
// Service imports:
import { getAPItoday, formatNumber, convertToPercentage } from '../../../services/utils';
import { useApiLmdData } from '../../../services/apiHooks';
import { SHORT_MONTHS } from '../../../services/config';
// Type imports:
import { IDefaultProps } from '../../../types/Types';
import { IApiHistoryTimeseriesShort, IApiHistoryTimeseriesShortDATA } from '../../../types/MarketTypes';

interface IChartDatum {
    x: number | undefined
    y: number | undefined
};

interface IChartDataInfo {
    name: string,
    data: IChartDatum[]
};


interface IOwnProps {
    symbol?: string,
    title?: string,
    height?: number
};

type Props = IOwnProps & IDefaultProps & React.HTMLAttributes<HTMLDivElement>;

const BondsChart: React.FC<Props> = ({
    symbol,
    height = 500,
    accessToken,
    refreshRateMs,
    ...props
}) => {
    const today = new Date();
    const from = new Date(today.getFullYear() - 5, today.getMonth() + 1, today.getDate());
    const [apiData] = useApiLmdData<IApiHistoryTimeseriesShortDATA>(
        `/v1/market_data/v1/tradables/${symbol}/history_timeseries_short/?from_date=${getAPItoday(from)}&to_date=${getAPItoday(today)}}`,
        accessToken,
        refreshRateMs
    );
    const [selectedRange, setSelectedRange] = useState(4);

    const handleData = () => {
        if (apiData.data === null || apiData.error) return null;
        let dataToReturn: IApiHistoryTimeseriesShort[] = [];
        for(let datum of apiData.data.data){
            dataToReturn.push({
                trading_date: datum[0],
                open: datum[1],
                open_yield: datum[2],
                high: datum[3],
                high_yield: datum[4],
                low: datum[5],
                low_yield: datum[6],
                close: datum[7],
                close_yield: datum[8],
                volume: datum[9],
                open_corrected: datum[10],
                high_corrected: datum[11],
                low_corrected: datum[12],
                close_corrected: datum[13],
                volume_corrected: datum[14],
            })
        }
        return dataToReturn;
    };

    const divRef = useRef<HTMLDivElement>(null);
    useEffect(() => {
        const chartDataYieldInfo: IChartDataInfo = {
            name: '',
            data: []
        }

        const chartDataPriceInfo: IChartDataInfo = {
            name: '',
            data: []
        }

        const chartDataVolumeInfo: IChartDataInfo = {
            name: '',
            data: []
        }

        const data = handleData();
        if (data !== null) {
            chartDataYieldInfo.name = symbol ?? '';
            chartDataPriceInfo.name = symbol ?? '';
            chartDataVolumeInfo.name = symbol ?? '';

            for (let datum of data) {
                chartDataYieldInfo.data.push({
                    x: datum.trading_date ? datum.trading_date : undefined,
                    y: datum.close_yield ?? undefined
                });

                chartDataPriceInfo.data.push({
                    x: datum.trading_date ? datum.trading_date : undefined,
                    y: datum.close ?? undefined
                });

                chartDataVolumeInfo.data.push({
                    x: datum.trading_date ? datum.trading_date : undefined,
                    y: datum.volume ?? undefined
                });
            }
            
        }

        Highcharts.setOptions({
            lang: {
                shortMonths: SHORT_MONTHS,
                resetZoom: 'Til baka',
                resetZoomTitle: ''
            }
        });

        if (data !== null && divRef.current !== null) {
            Highcharts.stockChart(divRef.current, {
                chart: {
                    height: height,
                    marginTop: 80,
                    reflow: true,
                    style: {
                        fontFamily: "Roboto"
                    },
                    zoomType: "xy",
                    selectionMarkerFill: '#4569EE18',
                    resetZoomButton: {
                        position: {
                            x: 0,
                            y: -75
                        },
                        theme: {
                            r: 6,
                            height: 28
                        }
                    },
                    plotBackgroundColor: {
                        linearGradient: {x1: 0, x2: 0, y1: 0, y2: 1},
                        stops: [
                            [0.05, '#FFF'],
                            [0.95, '#F8F8FA']
                        ]
                    }
                },
                exporting: {
                    buttons: {
                        contextButton: {
                            x: 30,
                            y: 36
                        }
                    }
                },
                credits: {
                    enabled: false
                },
                title: {
                    text: '',
                    align: 'center',
                    x: 60,
                    margin: -14
                },
                navigator: {
                    enabled: true,
                    adaptToUpdatedData: false,
                    handles: {
                        height: 22,
                        width: 6
                    },
                    maskFill: '#D4DEFF80',
                    series: {
                        lineWidth: 0,
                        opacity: 0
                    },
                    height: 20,
                    xAxis: {
                        labels: {
                            style: {
                                color: '#7B7C83',
                                fontSize: '10px'
                            },
                            y: -6
                        },
                        gridLineWidth: 0,
                        minTickInterval: 1000*60*60*24*365,
                        lineWidth: 0
                    },
                    yAxis: {
                        lineWidth: 0
                    },
                    outlineWidth: 0
                },
                scrollbar: {
                    enabled: false
                },
                tooltip: {
                    enabled: true,
                    formatter: function() {
                        return `<div class="chart-tooltip">
                            ${this.points?.reduce(function (s, point) {
                                if (point.series.name === 'Verð') {
                                    return `${s}<p class="tooltip-value"><span style="color: ${point.color}">${point.series.name}</span>: ${formatNumber(point.y, null, 3)}</p>`;
                                } else if (point.series.name === 'Krafa') {
                                    return `${s}<p class="tooltip-value"><span style="color: ${point.color}">${point.series.name}</span>: ${convertToPercentage((point.y) ?? null, true, 3)}</p>`;
                                } else {
                                    return `${s}<p class="tooltip-volume"><span style="color: ${point.color}">${point.series.name}</span>: ${formatNumber(point.y, null, 0)}</p>`
                                }
                                
                            }, `<p class="tooltip-date">${format(this.x, 'EEEE, dd. MMM yyyy', {locale: ISLocale})}</p>`)}
                        </div>`;
                    },
                    borderWidth: 0,
                    shadow: false,
                    useHTML: true,
                    shared: true
                },
                xAxis: [{
                    type: 'datetime',
                    title: {
                        text: null
                    },
                    minTickInterval: 1000*60*60*24*30,
                    tickWidth: 0
                }],
                yAxis: [{
                    title: {
                        text: 'Krafa',
                        offset: 52,
                        x: 5
                    },
                    labels: {
                        formatter: function() {
                            return convertToPercentage(this.value as number, true, 2) ?? this.value.toString();
                        },
                        x: -11,
                        y: 2
                    },
                    minTickInterval: 0.001,
                    gridLineDashStyle: 'Dash',
                    gridLineWidth: 1,
                    gridLineColor: '#D3D3D6',
                    opposite: false,
                    height: '70%'
                }, {
                    title: {
                        text: 'Verð',
                        offset: 52,
                        x: 5
                    },
                    minTickInterval: 1,
                    gridLineDashStyle: 'Dash',
                    gridLineWidth: 1,
                    gridLineColor: '#D3D3D6',
                    opposite: true,
                    height: '70%',
                    labels: {
                        x: -11,
                        y: 2
                    }
                }, {
                    title: {
                        text: 'Magn',
                        offset: 52,
                        x: 5
                    },
                    minTickInterval: 1,
                    gridLineDashStyle: 'Dash',
                    gridLineWidth: 1,
                    gridLineColor: '#D3D3D6',
                    opposite: false,
                    top: '76%',
                    height: '24%',
                    offset: 0,
                    labels: {
                        x: -11,
                        y: 2
                    }
                }],
                plotOptions: {
                    series: {
                        color: '#4569EE',
                        enableMouseTracking: true,
                        marker: {
                            enabled: false
                        },
                        animation: false,
                        turboThreshold: 0
                    }
                },
                legend: {
                    enabled: true,
                    align: 'left',
                    verticalAlign: 'top',
                    itemStyle: {
                        color: '#232530',
                        fontSize: '14px',
                        fontWeight: '400',
                        lineHeight: '26px'
                    },
                    x: 5,
                    y: 10,
                    labelFormatter: function() {
                        return `${symbol} ${this.name}`;
                    }
                },
                rangeSelector: {
                    allButtonsEnabled: true,
                    buttonPosition: {
                        align: 'right',
                        x: 0,
                        y: -80
                    },
                    buttons: [{
                        type: 'month',
                        count: 1,
                        text: '1 mán.',
                        title: '1 mánuður',
                        events: {
                            click: () => setSelectedRange(0)
                        }
                    }, {
                        type: 'month',
                        count: 3,
                        text: '3 mán.',
                        title: '3 mánuðir',
                        events: {
                            click: () => setSelectedRange(1)
                        }
                    }, {
                        type: 'month',
                        count: 6,
                        text: '6 mán.',
                        title: '6 mánuðir',
                        events: {
                            click: () => setSelectedRange(2)
                        }
                    }, {
                        type: 'ytd',
                        text: 'Þetta ár',
                        title: 'Þetta ár',
                        events: {
                            click: () => setSelectedRange(3)
                        }
                    }, {
                        type: 'year',
                        count: 1,
                        text: '1 ár',
                        title: '1 ár',
                        events: {
                            click: () => setSelectedRange(4)
                        }
                    }, {
                        type: 'year',
                        count: 3,
                        text: '3 ár',
                        title: '3 ár',
                        events: {
                            click: () => setSelectedRange(5)
                        }
                    }, {
                        type: 'year',
                        count: 5,
                        text: '5 ár',
                        title: '5 ár',
                        events: {
                            click: () => setSelectedRange(6)
                        }
                    }],
                    buttonSpacing: 5,
                    buttonTheme: {
                        r: 6,
                        height: 26,
                        width: 50
                    },
                    dropdown: 'never',
                    inputEnabled: false,
                    selected: selectedRange,
                    labelStyle: {
                        display: 'none'
                    }
                },
                responsive: {
                    rules: [{
                        condition: {
                            maxWidth: 369
                        },
                        chartOptions: {
                            rangeSelector: {
                                dropdown: 'always'
                            }
                        }
                    }, {
                        condition: {
                           callback: function() {
                               return window.matchMedia('(max-width: 767px)').matches;
                           }
                        },
                        chartOptions: {
                            chart: {
                                spacingLeft: 0,
                                spacingRight: 5,
                                marginTop: 40
                            },
                            legend: {
                                x: 10,
                                y: -20,
                                itemStyle: {
                                    fontSize: '12px'
                                }
                            },
                            navigator: {
                                margin: 13
                            },
                            rangeSelector: {
                                buttonPosition: {
                                    y: -40
                                },
                                buttonTheme: {
                                    width: 36,
                                    height: 24
                                }
                            },
                            xAxis: {
                                labels: {
                                    style: {
                                        fontSize: '9px'
                                    }
                                }
                            },
                            yAxis: [{
                                labels: {
                                    style: {
                                        fontSize: '9px'
                                    },
                                    x: -1
                                },
                                title: {
                                    offset: 27,
                                    x: 0,
                                    style: {
                                        fontSize: '10px'
                                    }
                                }
                            }, {
                                labels: {
                                    style: {
                                        fontSize: '9px'
                                    },
                                    x: -1
                                },
                                title: {
                                    offset: 27,
                                    x: 0,
                                    style: {
                                        fontSize: '10px'
                                    }
                                }
                            }, {
                                labels: {
                                    style: {
                                        fontSize: '9px'
                                    },
                                    x: -1
                                },
                                title: {
                                    offset: 27,
                                    x: 0,
                                    style: {
                                        fontSize: '10px'
                                    }
                                }
                            }]
                        }
                    }]
                },
                series: [{
                    type: 'line',
                    id: 'yield',
                    name: 'Krafa',
                    data: chartDataYieldInfo.data,
                    tooltip: {
                        valueDecimals: 2
                    },
                    yAxis: 0,
                    color: '#FF7697'
                }, {
                    type: 'line',
                    id: 'price',
                    name: 'Verð',
                    data: chartDataPriceInfo.data,
                    tooltip: {
                        valueDecimals: 2
                    },
                    yAxis: 1
                }, {
                    type: 'column',
                    id: 'volume',
                    name: 'Magn',
                    data: chartDataVolumeInfo.data,
                    yAxis: 2,
                    color: '#48A85D',
                    tooltip: {
                        valueDecimals: 2
                    }
                }]
            }, () => {});
        }
    }, [apiData, divRef.current]);
    return (
        <DisplayBox
            className='KCL_chart chart--bonds'
            title="Söguleg gögn"
            {...props}
        >
            {apiData.data === null
                ? <ChartLoading />
                : <div className="chart" ref={divRef}></div>
            }
        </DisplayBox>
    );
}

export default BondsChart;